import { EMBED_IFRAME_ALLOW } from "./constants";

// Clickk embed loader — phase 1.
//
// Served as a versioned IIFE from https://embed.clickk.com/v1/embed.js. Pages
// embed it like:
//
//   <script src="https://embed.clickk.com/v1/embed.js" data-video="abc123"></script>
//   <script src="https://embed.clickk.com/v1/embed.js" data-offer="xyz789"></script>
//
// For each <script data-video> or <script data-offer> the loader finds at
// DOMReady, it inserts an iframe pointing at app.clickk.com/embed/* with
// ?embed_mode=loader. It listens for clickk:resize postMessages (origin-checked
// against APP_ORIGIN) to size each iframe, and renders a "Powered by Clickk"
// backlink in parent-page HTML for video embeds — the link points to the fan
// view (/watch/<id>) and lives outside the iframe so it contributes to
// Clickk's domain authority.

declare const __LOADER_VERSION__: string;
declare const __APP_ORIGIN__: string;

(function () {
  try {
    const APP_ORIGIN = __APP_ORIGIN__;
    const LOADER_VERSION = __LOADER_VERSION__;
    const LOADED_MARKER = "data-clickk-loaded";
    const warn = (...args: unknown[]) => {
      if (typeof console !== "undefined" && typeof console.warn === "function") {
        console.warn("[clickk-loader]", ...args);
      }
    };

    type EntityType = "content" | "offer";

    type EmbedSpec = {
      script: HTMLScriptElement;
      entityType: EntityType;
      entityId: string;
    };

    // Track iframes we created so the message listener can route a resize
    // event back to the iframe it came from (event.source === contentWindow).
    type IframeEntry = { iframe: HTMLIFrameElement };
    const iframes: IframeEntry[] = [];

    const readSpec = (script: HTMLScriptElement): EmbedSpec | null => {
      const video = script.getAttribute("data-video");
      const offer = script.getAttribute("data-offer");
      if (video && offer) {
        warn("script has both data-video and data-offer; using data-video", script);
      }
      if (video) {
        const id = video.trim();
        if (!id) return null;
        return { script, entityType: "content", entityId: id };
      }
      if (offer) {
        const id = offer.trim();
        if (!id) return null;
        return { script, entityType: "offer", entityId: id };
      }
      return null;
    };

    const buildEmbedSrc = (spec: EmbedSpec): string => {
      const base =
        spec.entityType === "content"
          ? APP_ORIGIN + "/embed/" + encodeURIComponent(spec.entityId)
          : APP_ORIGIN + "/embed/offer/" + encodeURIComponent(spec.entityId);
      return base + "?embed_mode=loader";
    };

    const renderEmbed = (spec: EmbedSpec) => {
      const parent = spec.script.parentNode;
      if (!parent) {
        warn("script tag has no parent; cannot inject embed", spec.script);
        return;
      }

      const container = document.createElement("div");
      container.setAttribute("data-clickk-embed", spec.entityType);
      container.className = "clickk-embed-frame";
      container.style.width = "100%";
      container.style.boxSizing = "border-box";
      container.style.maxWidth = "100%";

      const shell = document.createElement("div");
      shell.className = "clickk-embed-shell";
      shell.style.position = "relative";
      shell.style.width = "100%";
      shell.style.borderRadius = "12px";
      shell.style.overflow = "hidden";
      shell.style.background = "#242424";

      const style = document.createElement("style");
      style.textContent = `
        .clickk-embed-frame .clickk-embed-iframe {
          width: 100%;
          max-width: 100%;
          display: block;
          transition: height 240ms ease, min-height 240ms ease;
          will-change: height;
        }

        @media (prefers-reduced-motion: reduce) {
          .clickk-embed-frame .clickk-embed-iframe {
            transition: none;
          }
        }
      `;

      const iframe = document.createElement("iframe");
      iframe.className = "clickk-embed-iframe";
      iframe.src = buildEmbedSrc(spec);
      iframe.title = spec.entityType === "content" ? "Clickk video" : "Clickk offer";
      iframe.loading = "lazy";
      iframe.setAttribute("allowfullscreen", "");
      iframe.setAttribute("allow", EMBED_IFRAME_ALLOW);
      iframe.style.display = "block";
      iframe.style.width = "100%";
      iframe.style.maxWidth = "100%";
      iframe.style.border = "0";
      if (spec.entityType === "content") {
        // Initial first-paint shape only. The embed page posts its rendered
        // height after mount and after container/layout changes; once that
        // arrives, the message listener below owns the real iframe height.
        iframe.style.background = "#242424";
        iframe.style.borderRadius = "12px";
        iframe.style.aspectRatio = "1200 / 570";
        iframe.style.height = "auto";
        iframe.style.minHeight = "320px";
        iframe.style.maxHeight = "none";
      } else {
        iframe.style.height = "600px";
      }

      shell.appendChild(style);
      shell.appendChild(iframe);
      container.appendChild(shell);

      // Backlink in parent-page HTML — a real <a> outside the iframe so it
      // contributes to clickk.com domain authority (an iframe-internal link
      // wouldn't). Points to the fan view rather than the marketing root so
      // the click takes the viewer to the content they were just watching.
      // Offers have no fan-side page yet, so we skip the link there (revisit
      // when a /offer/[id] fan page exists).
      if (spec.entityType === "content") {
        const byline = document.createElement("div");
        byline.style.padding = "6px 6px 0 0";
        byline.style.display = "flex";
        byline.style.justifyContent = "flex-end";
        byline.style.fontFamily =
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif";
        byline.style.fontSize = "12px";
        byline.style.lineHeight = "1.4";
        byline.style.color = "#666";

        const link = document.createElement("a");
        link.href = APP_ORIGIN + "/watch/" + encodeURIComponent(spec.entityId);
        link.target = "_blank";
        link.rel = "noopener";
        link.textContent = "Powered by Clickk";
        link.style.color = "inherit";
        link.style.textDecoration = "none";

        byline.appendChild(link);
        container.appendChild(byline);
      }

      parent.insertBefore(container, spec.script.nextSibling);
      spec.script.setAttribute(LOADED_MARKER, "1");
      iframes.push({ iframe });
    };

    const init = () => {
      const scripts = document.querySelectorAll<HTMLScriptElement>(
        "script[data-video], script[data-offer]"
      );
      for (let i = 0; i < scripts.length; i++) {
        const script = scripts[i];
        if (script.getAttribute(LOADED_MARKER) === "1") continue;
        try {
          const spec = readSpec(script);
          if (spec) renderEmbed(spec);
        } catch (err) {
          warn("failed to render embed", err);
        }
      }
    };

    const onMessage = (event: MessageEvent) => {
      try {
        // Origin check — only trust messages from the embed app itself. Same
        // pattern as CLI-739's OAuth handshake. The iframe posts with
        // targetOrigin "*" because customer pages embed us on arbitrary
        // origins, so the trust check lives on this side.
        if (event.origin !== APP_ORIGIN) return;

        const data = event.data as unknown;
        if (!data || typeof data !== "object") return;
        const payload = data as { type?: unknown; height?: unknown };
        if (payload.type !== "clickk:resize") return;
        const height = payload.height;
        if (typeof height !== "number" || !isFinite(height) || height < 0) return;

        for (let i = 0; i < iframes.length; i++) {
          const entry = iframes[i];
          if (event.source && event.source === entry.iframe.contentWindow) {
            entry.iframe.style.aspectRatio = "auto";
            entry.iframe.style.width = "100%";
            entry.iframe.style.maxWidth = "100%";
            entry.iframe.style.margin = "";
            entry.iframe.style.height = height + "px";
            entry.iframe.style.minHeight = height + "px";
            entry.iframe.style.maxHeight = "none";
            return;
          }
        }
      } catch (err) {
        warn("resize handler failed", err);
      }
    };

    window.addEventListener("message", onMessage);

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", init, { once: true });
    } else {
      init();
    }

    // Tag the loader version on the document so devtools / support can verify
    // which version is on a page without poking through the bundle.
    document.documentElement.setAttribute("data-clickk-loader", LOADER_VERSION);
  } catch (err) {
    if (typeof console !== "undefined" && typeof console.warn === "function") {
      console.warn("[clickk-loader] boot failed", err);
    }
  }
})();
